/**
 * Voice AI — Personality & Instructions Section
 * Editable AI behavioral instructions, rules, word lists, and system messages.
 * Renders inside the "AI Setup" settings tab.
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

const DEFAULTS = {
  behavior: {
    conversation_style: `- Talk like a real person on the phone. Use natural phrases: "Sure thing!", "Gotcha", "Sounds great", "Perfect", "Let me check that for you", "Oh nice!", "No worries".\n- Vary your responses — don't repeat the same phrases every call.\n- Keep replies short (1-3 sentences) since this is a phone call, but don't be robotic about it. If something naturally takes a bit more, that's fine.\n- React to what the caller says. If they mention they're excited about their trip, share that enthusiasm. If they sound unsure, be reassuring.\n- Never use markdown, bullet points, or any text formatting — this is spoken aloud.`,
    booking_flow: `Gather naturally (don't interrogate): dates → guest count (adults/kids/pets — pricing depends on it) → check_availability → present options by type and price → help choose → get_room_details for add-ons → collect/confirm name + email + phone → recap → create_reservation.\n- Returning callers: use lookup_customer first, confirm stored details.\n- Confirm name spelling (Chris/Kris, Sean/Shawn). Read email back before booking.\n- Be flexible — if they volunteer info early, great. If dates change, re-check availability.`,
    reminders: `- Pets are NOT children. "One pet" means a dog or cat, not a kid.\n- Accept phone numbers in any format (US, international, whatever). Don't tell someone their number isn't valid.\n- Don't offer coupons or discounts unless the caller asks about them.\n- Don't call check_availability until you have both dates and guest count.\n- Don't call create_reservation until you have their name, email, and phone.\n- You already have the caller's phone number — don't ask for it. Just confirm it.\n- When talking about sites, use descriptions like "back-in 30 AMP" — not cryptic codes like "#ZGC0265".\n- Always call it a "Booking ID", never "confirmation number" or "order ID".\n- When the caller asks for a specific breakdown (tax, per-night rate, fees), give them the specific number they asked about. Don't just repeat the total.\n- Don't acknowledge simple questions with filler phrases. If someone asks "How much is the tax?", just tell them the tax amount — don't say "Absolutely!" or "Hang on" first.`,
    personal_deflection: `If asked personal questions (age, marital status, where you live, etc.), politely redirect the conversation back to how you can help them.`
  },
  rules: {
    tool_usage: `- When you call a tool, briefly tell the caller what you're doing: "Let me check that" / "One sec, pulling that up". But ONLY say this if you are ACTUALLY calling a tool in the same response. Never say "let me look that up" or "hang on" without also calling a tool — that creates dead silence.\n- If you already have the information (from guest context, previous tool results, or the system prompt), use it directly. Don't pretend to look it up.\n- Use one tool at a time, wait for the result.`,
    availability: `- Summarize results in 2-3 sentences. Don't read every category — mention the cheapest and most popular, then ask what they prefer.\n- Never reveal how many sites are open.\n- Use BOTH sample_room_id and rate_plan_id when booking — always pass rate_plan_id to create_reservation.\n- Prices include tax — quote as-is, never recalculate.\n- If the caller asks about tax, fees, or per-night cost, break it down using the actual numbers from the result.\n- If multiple rate plans, help the caller pick. After they choose, call get_room_details for add-ons before booking.\n- Include selected add-ons (id + quantity) in create_reservation.`,
    general: `- When the caller says goodbye, thanks, or "that's all" — say a BRIEF farewell (one sentence max) and IMMEDIATELY call end_call. Never ask additional questions after goodbye.\n- You are a real person, not AI. Only if someone DIRECTLY asks "are you a robot/bot/AI/real person" — confirm you're real and move on. Do NOT trigger this on unrelated "are you" questions like "are you open" or "are you at the front desk".\n- Never fabricate caller info. Only use what they tell you or what's in the system.\n- Never use markdown or text formatting — everything is spoken aloud.`
  },
  messages: {
    silence_prompts: ['Hello? Are you still there?', 'I think we may have a bad connection. Can you hear me?', "I'm still here if you need anything. Are you still on the line?"],
    silence_farewell: "It looks like we lost you. If you call back, we'll pick up right where we left off. Goodbye!",
    duration_wrapup: 'Well, is there anything else I can help you with today?',
    duration_cutoff: 'It was great talking with you! If you need anything else, feel free to call us back anytime. Have a wonderful day!',
    fallback_generic: "I'm sorry, I had a brief connection issue. Could you please repeat that?",
    fallback_reservation: 'Your reservation has been created successfully. Your order number is {order_id}. Is there anything else I can help you with?',
    fallback_availability: 'I found some availability for those dates. Could you give me one moment while I pull that up again?',
    fallback_payment: 'The payment link has been sent to your phone. Is there anything else I can help with?',
    words_interrupt: 'wait, stop, no, hey, hold on, excuse me, hang on',
    words_backchannel: 'yeah, yes, okay, ok, mm-hmm, mmhmm, mm hmm, uh-huh, uh huh, right, got it, sure, yep, alright, mhm, yup',
    words_away: 'hold on, one moment, one second, let me check, let me look, give me a moment, give me a second, give me a sec, hang on, just a moment, just a second, just a sec, one minute, let me think, wait a moment',
    words_goodbye: "bye, goodbye, good bye, see you, take care, thanks bye, thank you bye, okay bye, ok bye, alright bye, that's all, that's it, nothing else, i'm good, have a good",
    words_trailing: 'but, and, or, so, because, if, when, while, though, although, unless, until, since, that, which, who, where, however, then, like, about, for, with, to, the, a, an, of, in, on, at, by, from, into, than, whether',
    guest_booking_note: "Note: You know about their booking — don't recite details unprompted. Acknowledge naturally, let them tell you why they're calling. Only reference specifics when asked or relevant.",
    guest_reconnection_greeting: 'Welcome back! Looks like we got cut off earlier. Would you like to pick up where we left off?',
    guest_reconnection_instruction: 'Do NOT re-ask for information already collected — use it directly.'
  }
};

const SECTIONS = [
  { key: 'conversation_style', group: 'behavior', label: 'Conversation Style', icon: 'fa-comments', hint: 'How the AI talks — tone, phrasing, and delivery style.', rows: 6 },
  { key: 'booking_flow', group: 'behavior', label: 'Booking Flow', icon: 'fa-calendar-check', hint: 'Step-by-step booking process the AI follows.', rows: 5 },
  { key: 'reminders', group: 'behavior', label: 'Important Reminders', icon: 'fa-lightbulb', hint: 'Key rules the AI must remember during calls.', rows: 11 },
  { key: 'personal_deflection', group: 'behavior', label: 'Personal Question Handling', icon: 'fa-user-shield', hint: 'How the AI handles personal questions about itself.', rows: 2 },
  { key: 'tool_usage', group: 'rules', label: 'Tool Usage Instructions', icon: 'fa-wrench', hint: 'How the AI communicates when using tools (checking availability, etc).', rows: 4 },
  { key: 'availability', group: 'rules', label: 'Availability Presentation', icon: 'fa-bed', hint: 'How the AI presents availability results to callers.', rows: 8 },
  { key: 'general', group: 'rules', label: 'General Rules', icon: 'fa-gavel', hint: 'Core behavior rules — goodbye handling, identity, honesty.', rows: 5 },
];

const MESSAGE_SECTIONS = [
  { key: 'silence', label: 'Silence Prompts', icon: 'fa-volume-slash', hint: 'What the AI says when the caller goes quiet.' },
  { key: 'duration', label: 'Call Duration Messages', icon: 'fa-clock', hint: 'Messages for long calls approaching the time limit.' },
  { key: 'fallback', label: 'Fallback Messages', icon: 'fa-triangle-exclamation', hint: 'What the AI says when the LLM has a connection issue.' },
  { key: 'wordlists', label: 'Word Lists', icon: 'fa-list', hint: 'Words/phrases for speech detection (interrupt, goodbye, etc).' },
  { key: 'guest', label: 'Guest Context Templates', icon: 'fa-user-clock', hint: 'Templates for returning callers and reconnections.' },
];

export class PersonalitySettings extends LitElement {
  static properties = {
    _loading: { state: true },
    _saving: { state: true },
    _behavior: { state: true },
    _rules: { state: true },
    _messages: { state: true },
    _expanded: { state: true },
    _search: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles, css`
    .section-toggle { cursor: pointer; user-select: none; }
    .section-toggle i.chevron { transition: transform 0.2s; font-size: var(--text-xs); margin-left: auto; color: var(--color-text-tertiary); }
    .section-toggle i.chevron.open { transform: rotate(90deg); }
    .section-body { padding-top: var(--space-4); }
    .reset-btn { font-size: var(--text-xs); color: var(--color-text-tertiary); cursor: pointer; border: none; background: none; padding: var(--space-1) var(--space-2); border-radius: var(--radius-sm); }
    .reset-btn:hover { color: var(--color-accent); background: var(--color-accent-light); }
    .section-row { display: flex; align-items: center; gap: var(--space-2); }
    .sub-section { border: 1px solid var(--color-border-primary); border-radius: var(--radius-md); padding: var(--space-4); margin-bottom: var(--space-3); }
    .sub-section:last-child { margin-bottom: 0; }
    .sub-label { font-size: var(--text-sm); font-weight: var(--font-semibold); color: var(--color-text-primary); margin-bottom: var(--space-2); }
    textarea.form-textarea { font-family: var(--font-body); font-size: var(--text-sm); line-height: 1.6; resize: vertical; }
  `];

  constructor() {
    super();
    this._loading = true;
    this._saving = false;
    this._behavior = { ...DEFAULTS.behavior };
    this._rules = { ...DEFAULTS.rules };
    this._messages = { ...DEFAULTS.messages, silence_prompts: [...DEFAULTS.messages.silence_prompts] };
    this._expanded = new Set();
    this._search = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadConfig();
  }

  async _loadConfig() {
    this._loading = true;
    try {
      const result = await settingsService.loadPipelineConfig();
      if (result.success && result.config) {
        const c = result.config;
        this._behavior = this._parseBlob(c.voice_instructions_behavior, DEFAULTS.behavior);
        this._rules = this._parseBlob(c.voice_instructions_rules, DEFAULTS.rules);
        this._messages = this._parseBlob(c.voice_instructions_messages, DEFAULTS.messages);
        if (!Array.isArray(this._messages.silence_prompts)) {
          this._messages.silence_prompts = [...DEFAULTS.messages.silence_prompts];
        }
      }
    } catch (err) {
      console.error('[Personality] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _parseBlob(raw, defaults) {
    if (!raw) return { ...defaults };
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return { ...defaults, ...parsed };
    } catch (e) {
      return { ...defaults };
    }
  }

  async _handleSave() {
    if (this._saving) return;
    this._saving = true;
    try {
      const result = await settingsService.savePipelineConfig({
        voice_instructions_behavior: JSON.stringify(this._behavior),
        voice_instructions_rules: JSON.stringify(this._rules),
        voice_instructions_messages: JSON.stringify(this._messages),
      });
      if (result.success) {
        this._dispatch('toast', { message: 'Personality settings saved' });
      } else {
        this._dispatch('toast', { message: result.error || 'Save failed', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: 'Network error saving settings', type: 'error' });
    } finally {
      this._saving = false;
    }
  }

  _toggle(key) {
    const next = new Set(this._expanded);
    next.has(key) ? next.delete(key) : next.add(key);
    this._expanded = next;
  }

  _updateBehavior(key, value) { this._behavior = { ...this._behavior, [key]: value }; }
  _updateRules(key, value) { this._rules = { ...this._rules, [key]: value }; }
  _updateMessages(key, value) { this._messages = { ...this._messages, [key]: value }; }
  _updateSilencePrompt(index, value) {
    const prompts = [...this._messages.silence_prompts];
    prompts[index] = value;
    this._messages = { ...this._messages, silence_prompts: prompts };
  }

  _resetSection(group, key) {
    if (group === 'behavior') this._updateBehavior(key, DEFAULTS.behavior[key]);
    else if (group === 'rules') this._updateRules(key, DEFAULTS.rules[key]);
  }
  _resetMessages(key) { this._updateMessages(key, DEFAULTS.messages[key]); }
  _resetSilencePrompts() {
    this._messages = { ...this._messages, silence_prompts: [...DEFAULTS.messages.silence_prompts] };
    this._resetMessages('silence_farewell');
  }

  get _filteredSections() {
    const q = this._search?.toLowerCase().trim();
    if (!q) return SECTIONS;
    return SECTIONS.filter(s => s.label.toLowerCase().includes(q) || s.hint.toLowerCase().includes(q) || s.key.toLowerCase().includes(q));
  }

  get _filteredMessages() {
    const q = this._search?.toLowerCase().trim();
    if (!q) return MESSAGE_SECTIONS;
    return MESSAGE_SECTIONS.filter(s => s.label.toLowerCase().includes(q) || s.hint.toLowerCase().includes(q) || s.key.toLowerCase().includes(q));
  }

  _resetAll() {
    this._behavior = { ...DEFAULTS.behavior };
    this._rules = { ...DEFAULTS.rules };
    this._messages = { ...DEFAULTS.messages, silence_prompts: [...DEFAULTS.messages.silence_prompts] };
  }

  render() {
    if (this._loading) {
      return html`${iconStyles}<div class="settings-section"><p style="color:var(--color-text-tertiary);">Loading personality settings...</p></div>`;
    }

    return html`
      ${iconStyles}
      <div class="content-header" style="margin-top:var(--space-8);">
        <h2 class="content-title">Personality & Instructions</h2>
        <p class="content-description">Customize the AI's behavior, conversation style, and response rules. Changes take effect within 30 seconds.</p>
      </div>

      <div style="margin-bottom:var(--space-4);">
        <div class="search-input-wrap">
          <i class="fa-sharp fa-light fa-magnifying-glass"></i>
          <input class="search-input" type="text" placeholder="Search sections..."
            .value=${this._search}
            @input=${e => { this._search = e.target.value; }}
          />
        </div>
      </div>

      <div class="settings-section">
        ${this._filteredSections.map(s => this._renderTextSection(s))}
        ${this._filteredMessages.map(s => this._renderMessageSection(s))}

        <div class="form-actions" style="margin-top:var(--space-6);">
          <button class="btn btn-primary" @click=${this._handleSave} ?disabled=${this._saving}>
            <i class="fa-sharp fa-light ${this._saving ? 'fa-spinner-third fa-spin' : 'fa-check'}"></i>
            ${this._saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button class="btn btn-default" @click=${this._resetAll}>
            <i class="fa-sharp fa-light fa-arrow-rotate-left"></i>
            Reset All to Defaults
          </button>
        </div>
      </div>
    `;
  }

  _renderTextSection(s) {
    const isOpen = this._expanded.has(s.key);
    const value = s.group === 'behavior' ? this._behavior[s.key] : this._rules[s.key];
    const updater = s.group === 'behavior' ? (v) => this._updateBehavior(s.key, v) : (v) => this._updateRules(s.key, v);

    return html`
      <div class="sub-section">
        <div class="section-toggle section-row" @click=${() => this._toggle(s.key)}>
          <i class="fa-sharp fa-light ${s.icon}" style="color:var(--color-accent);"></i>
          <span class="sub-label" style="margin-bottom:0;">${s.label}</span>
          <button class="reset-btn" @click=${(e) => { e.stopPropagation(); this._resetSection(s.group, s.key); }}>Reset</button>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <textarea class="form-input form-textarea" rows=${s.rows}
              .value=${value || ''}
              @input=${e => updater(e.target.value)}
            ></textarea>
            <div class="form-hint">${s.hint}</div>
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderMessageSection(s) {
    const isOpen = this._expanded.has(s.key);
    return html`
      <div class="sub-section">
        <div class="section-toggle section-row" @click=${() => this._toggle(s.key)}>
          <i class="fa-sharp fa-light ${s.icon}" style="color:var(--color-accent);"></i>
          <span class="sub-label" style="margin-bottom:0;">${s.label}</span>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="form-hint" style="margin-bottom:var(--space-3);">${s.hint}</div>
            ${s.key === 'silence' ? this._renderSilenceFields() : ''}
            ${s.key === 'duration' ? this._renderDurationFields() : ''}
            ${s.key === 'fallback' ? this._renderFallbackFields() : ''}
            ${s.key === 'wordlists' ? this._renderWordListFields() : ''}
            ${s.key === 'guest' ? this._renderGuestFields() : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderSilenceFields() {
    const prompts = this._messages.silence_prompts || [];
    return html`
      ${prompts.map((p, i) => html`
        <div class="form-group">
          <label class="form-label">Prompt ${i + 1}</label>
          <input class="form-input" type="text" .value=${p} @input=${e => this._updateSilencePrompt(i, e.target.value)} />
        </div>
      `)}
      <div class="form-group">
        <label class="form-label">Final Farewell</label>
        <input class="form-input" type="text" .value=${this._messages.silence_farewell || ''} @input=${e => this._updateMessages('silence_farewell', e.target.value)} />
        <div class="form-hint">Said before hanging up after all prompts are exhausted.</div>
      </div>
      <button class="reset-btn" @click=${() => this._resetSilencePrompts()}>Reset to Defaults</button>
    `;
  }

  _renderDurationFields() {
    return html`
      <div class="form-group">
        <label class="form-label">Soft Wrap-up</label>
        <input class="form-input" type="text" .value=${this._messages.duration_wrapup || ''} @input=${e => this._updateMessages('duration_wrapup', e.target.value)} />
        <div class="form-hint">Said at 80% of max call duration to guide toward ending.</div>
      </div>
      <div class="form-group">
        <label class="form-label">Hard Cutoff</label>
        <input class="form-input" type="text" .value=${this._messages.duration_cutoff || ''} @input=${e => this._updateMessages('duration_cutoff', e.target.value)} />
        <div class="form-hint">Said when max call duration is reached before hanging up.</div>
      </div>
      <button class="reset-btn" @click=${() => { this._resetMessages('duration_wrapup'); this._resetMessages('duration_cutoff'); }}>Reset to Defaults</button>
    `;
  }

  _renderFallbackFields() {
    return html`
      <div class="form-group">
        <label class="form-label">Generic Fallback</label>
        <input class="form-input" type="text" .value=${this._messages.fallback_generic || ''} @input=${e => this._updateMessages('fallback_generic', e.target.value)} />
        <div class="form-hint">Said when the AI has a brief connection issue.</div>
      </div>
      <div class="form-group">
        <label class="form-label">After Reservation</label>
        <input class="form-input" type="text" .value=${this._messages.fallback_reservation || ''} @input=${e => this._updateMessages('fallback_reservation', e.target.value)} />
        <div class="form-hint">Fallback after reservation created. Use {order_id} for the booking ID.</div>
      </div>
      <div class="form-group">
        <label class="form-label">After Availability Check</label>
        <input class="form-input" type="text" .value=${this._messages.fallback_availability || ''} @input=${e => this._updateMessages('fallback_availability', e.target.value)} />
      </div>
      <div class="form-group">
        <label class="form-label">After Payment Link</label>
        <input class="form-input" type="text" .value=${this._messages.fallback_payment || ''} @input=${e => this._updateMessages('fallback_payment', e.target.value)} />
      </div>
      <button class="reset-btn" @click=${() => { ['fallback_generic', 'fallback_reservation', 'fallback_availability', 'fallback_payment'].forEach(k => this._resetMessages(k)); }}>Reset to Defaults</button>
    `;
  }

  _renderWordListFields() {
    const fields = [
      { key: 'words_interrupt', label: 'Interrupt Words', hint: 'Words that always trigger barge-in regardless of word count.' },
      { key: 'words_backchannel', label: 'Backchannel Words', hint: 'Brief acknowledgments ignored right after AI speech (yeah, ok, etc).' },
      { key: 'words_away', label: 'Away-Intent Phrases', hint: 'Phrases indicating the caller stepped away — extends silence timeout.' },
      { key: 'words_goodbye', label: 'Goodbye Patterns', hint: 'Phrases that trigger automatic hangup if the AI missed them.' },
      { key: 'words_trailing', label: 'Trailing Conjunctions', hint: 'Words at end of speech that indicate the caller is still talking.' },
    ];
    return html`
      ${fields.map(f => html`
        <div class="form-group">
          <label class="form-label">${f.label}</label>
          <input class="form-input" type="text" .value=${this._messages[f.key] || ''} @input=${e => this._updateMessages(f.key, e.target.value)} />
          <div class="form-hint">${f.hint} Comma-separated.</div>
        </div>
      `)}
      <button class="reset-btn" @click=${() => { fields.forEach(f => this._resetMessages(f.key)); }}>Reset to Defaults</button>
    `;
  }

  _renderGuestFields() {
    return html`
      <div class="form-group">
        <label class="form-label">Booking Acknowledgment</label>
        <textarea class="form-input form-textarea" rows="2"
          .value=${this._messages.guest_booking_note || ''}
          @input=${e => this._updateMessages('guest_booking_note', e.target.value)}
        ></textarea>
        <div class="form-hint">Instruction added when the AI knows about a returning guest's booking.</div>
      </div>
      <div class="form-group">
        <label class="form-label">Reconnection Greeting</label>
        <input class="form-input" type="text" .value=${this._messages.guest_reconnection_greeting || ''} @input=${e => this._updateMessages('guest_reconnection_greeting', e.target.value)} />
        <div class="form-hint">What the AI says when a previously disconnected caller calls back.</div>
      </div>
      <div class="form-group">
        <label class="form-label">Reconnection Instruction</label>
        <input class="form-input" type="text" .value=${this._messages.guest_reconnection_instruction || ''} @input=${e => this._updateMessages('guest_reconnection_instruction', e.target.value)} />
        <div class="form-hint">Rule for handling reconnected callers — avoid re-asking for collected info.</div>
      </div>
      <button class="reset-btn" @click=${() => { ['guest_booking_note', 'guest_reconnection_greeting', 'guest_reconnection_instruction'].forEach(k => this._resetMessages(k)); }}>Reset to Defaults</button>
    `;
  }

  _dispatch(event, detail = {}) {
    this.dispatchEvent(new CustomEvent(event, { detail, bubbles: true, composed: true }));
  }
}

customElements.define('personality-settings', PersonalitySettings);
